import { useCallback, useState } from 'react';

export const COLLAPSED_KEY = 'areeb-sidebar-collapsed';

/**
 * Whether the desktop sidebar is a 72px icon rail.
 *
 * Expanded is the default: someone opening the app for the first time should
 * see what the sections are called, and the rail is the optimisation they opt
 * into afterwards.
 *
 * Every localStorage call is guarded. Reading storage throws outright in a
 * private window with site data blocked, and a shell that cannot render
 * because it could not remember a sidebar width is a far worse failure than a
 * forgotten preference.
 */
function readStored(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

export function useCollapsedSidebar(): [boolean, (next: boolean) => void] {
  const [collapsed, setCollapsed] = useState<boolean>(readStored);

  const set = useCallback((next: boolean) => {
    setCollapsed(next);
    try {
      localStorage.setItem(COLLAPSED_KEY, String(next));
    } catch {
      // The preference is lost for this browser; the session still works.
    }
  }, []);

  return [collapsed, set];
}
